/**
 * Folder-aware file collection for the bulk importer. Two entry points:
 *  - filesFromInputFiles(fileList): a FileList from <input webkitdirectory multiple>
 *  - filesFromDataTransfer(dataTransfer): a drag-and-drop payload (folders + files)
 *
 * Both return a flat array of { file, relativePath } where relativePath is the
 * POSIX-style path WITHIN the dropped/picked folder (e.g. "SFX/Metal/hit.wav").
 *
 * MEMORY-SAFE: only File handles + paths are collected here — no file contents
 * are ever read (no arrayBuffer / hashing), so selecting a 1000+-file pack stays
 * cheap. Hashing / previews are deferred to the wizard, on demand.
 */

/** Junk files asset packs / OSes scatter around — dropped silently. */
const IGNORED_NAMES = new Set([".DS_Store", "Thumbs.db", "desktop.ini"]);

/**
 * True for OS/junk artifacts that should never be imported. Exported so the zip
 * importer (utils/zipImport.js) applies the SAME filter per path component —
 * keeping a single source of truth for what counts as junk.
 */
export function isIgnored(name) {
    return !name || IGNORED_NAMES.has(name) || name.startsWith("._") || name === "__MACOSX";
}

/**
 * Collect files from an <input type="file" webkitdirectory multiple> selection.
 * `webkitRelativePath` is "<rootFolder>/sub/file.ext" for a directory pick, or
 * "" for individually-picked files (then we fall back to the bare name).
 */
export function filesFromInputFiles(fileList) {
    const out = [];
    for (const file of Array.from(fileList || [])) {
        if (isIgnored(file.name)) continue;
        const rel =
            file.webkitRelativePath && file.webkitRelativePath.length > 0
                ? file.webkitRelativePath
                : file.name;
        out.push({ file, relativePath: rel });
    }
    return out;
}

/**
 * Collect files from a drag-and-drop DataTransfer, recursing into dropped
 * folders via the (non-standard but universally-supported) entries API. Falls
 * back to the flat `.files` list when the entries API is unavailable.
 */
export async function filesFromDataTransfer(dataTransfer) {
    const items = dataTransfer && dataTransfer.items ? Array.from(dataTransfer.items) : [];
    const entries = items
        .map((it) => (typeof it.webkitGetAsEntry === "function" ? it.webkitGetAsEntry() : null))
        .filter(Boolean);

    if (entries.length === 0) {
        const files = dataTransfer && dataTransfer.files ? Array.from(dataTransfer.files) : [];
        return files.filter((f) => !isIgnored(f.name)).map((f) => ({ file: f, relativePath: f.name }));
    }

    const out = [];
    for (const entry of entries) {
        // eslint-disable-next-line no-await-in-loop -- intentional: bound memory by
        // walking one top-level entry at a time rather than buffering everything.
        await walkEntry(entry, "", out);
    }
    return out;
}

async function walkEntry(entry, prefix, out) {
    if (!entry) return;

    if (entry.isFile) {
        const file = await fileFromEntry(entry);
        if (file && !isIgnored(file.name)) {
            out.push({ file, relativePath: prefix ? `${prefix}/${file.name}` : file.name });
        }
        return;
    }

    if (entry.isDirectory) {
        if (isIgnored(entry.name)) return;
        const childPrefix = prefix ? `${prefix}/${entry.name}` : entry.name;
        const children = await readAllDirectoryEntries(entry);
        for (const child of children) {
            // eslint-disable-next-line no-await-in-loop
            await walkEntry(child, childPrefix, out);
        }
    }
}

function fileFromEntry(fileEntry) {
    return new Promise((resolve) => {
        fileEntry.file(
            (f) => resolve(f),
            () => resolve(null)
        );
    });
}

/**
 * `readEntries` returns AT MOST ~100 entries per call and MUST be called
 * repeatedly until it returns an empty array — a single call silently drops the
 * rest of a large directory (a real bug class). This drains it fully.
 */
function readAllDirectoryEntries(dirEntry) {
    const reader = dirEntry.createReader();
    return new Promise((resolve, reject) => {
        const all = [];
        const readBatch = () => {
            reader.readEntries(
                (batch) => {
                    if (!batch || batch.length === 0) {
                        resolve(all);
                        return;
                    }
                    all.push(...batch);
                    readBatch(); // keep reading until the directory is drained
                },
                (err) => reject(err)
            );
        };
        readBatch();
    });
}
